// utils/NotificationsAPI.ts
import { store } from '../context/store';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  soundUrl?: string;
  onClick?: () => void;
}

export class NotificationsAPI {
  /** Get latest app state for logo/icon dynamically */
  private static getDefaultIcon(): string {
    const state = store.getState();
    return (
      state.app.hostMetadata?.favicon ||
      state.app.hostMetadata?.logo ||
      '/favicon.png'
    );
  }

  /** Ask for permission dynamically when needed */
  public static async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications.');
      return 'denied';
    }

    if (Notification.permission === 'default') {
      return Notification.requestPermission();
    }

    return Notification.permission;
  }

  /** Should be called once on app load */
  public static async init(): Promise<void> {
    const permission = await this.requestPermission();

    if (permission !== 'granted') {
      console.warn(`Notification permission: ${permission}`);
    }
  }

  /** Fire notification if allowed */
  public static async send({
    title,
    body,
    icon,
    soundUrl,
    onClick,
  }: NotificationPayload): Promise<void> {
    if (!('Notification' in window)) {
      console.warn('Notifications are not supported in this browser.');
      return;
    }

    const permission = await this.requestPermission();

    if (permission !== 'granted') {
      console.warn('Notification permission not granted.');
      return;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: icon || this.getDefaultIcon(),
        silent: !soundUrl,
        // requireInteraction: true, // Uncomment to make it sticky
      });

      if (onClick) {
        notification.onclick = (event) => {
          event.preventDefault();
          onClick();
        };
      }

      // Optional: Auto-close notification
      setTimeout(() => notification.close(), 10000);

      // Optional: Play sound in browser context
      if (soundUrl) {
        const audio = new Audio(soundUrl);
        audio
          .play()
          .catch((err) =>
            console.warn('Custom notification sound failed to play:', err),
          );
      }
    } catch (error) {
      console.error('Notification failed:', error);
    }
  }
}
